# F500 In-App Documentation Phase 1 — viewer page
# Route: /docs
# Audience: Any authenticated user
# Ref: SPECIFICATION.md §4, DESIGN.md §7
#
# Two-pane layout: left = search + doc tree; right = md-view Markdown renderer.
# Header nav binding deferred to F71 merge (DESIGN D-7). Route reachable by URL.

page-docs:
  type: Page
  archetype: detail
  meta:
    title: $t.docs.meta_title
    route: /docs
    layout: shell
    auth: required
  data:
    # Doc tree: loaded on mount, driven by locale context.
    # The emitter wires locale from the active user preference.
    tree:
      model: DocsTree
      empty: docs-page-empty-state
      params:
        locale: $locale
      refresh: on_mount
      data_states:
        loading:
          skeleton_count: 6
        error:
          icon: $icon.warning
          title: $t.docs.tree_error
          retry: true
    # Current page: fetched when user selects a leaf in the tree.
    # Triggered by the docs-select custom event emitted from the tree component.
    # `path: $state.selected_path` makes the selection URL state, under the param's
    # name: a selection writes `?path=<path>` with a history entry, and a `?path=` on
    # load, reload or back/forward seeds selected_path and fetches this entry.
    page:
      model: DocsPage
      params:
        path: $state.selected_path
        locale: $locale
      trigger: docs-select
      data_states:
        loading:
          type: skeleton
        error:
          icon: $icon.warning
          title: $t.docs.page_error
          retry: true
    # Full-text "search page contents" results (RAG). This is the EXPLICIT,
    # separated second search mechanism: the instant title typeahead runs
    # client-side in xp87-docs-tree (no fetch); only when the operator clicks
    # the tree's "search page contents" button does it dispatch
    # docs-fulltext-search, which triggers this RAG fetch. No debounce — the
    # fetch is a deliberate click, not a per-keystroke action.
    search_results:
      model: DocsSearchResults
      params:
        q: $state.search_query
        locale: $locale
      trigger: docs-fulltext-search
      data_states:
        loading:
          type: skeleton
        error:
          icon: $icon.warning
          title: $t.docs.tree_error
          retry: true
        empty:
          icon: $icon.magnifying-glass
          title: $t.docs.no_results
  spatial:
    display: flex
    direction: column
    height: 100%
    overflow: hidden
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - docs-page-header
    - docs-body

# ── Page header ─────────────────────────────────────────────────────────────

docs-page-header:
  type: PageHeader
  content: $t.docs.title

# ── Two-pane body ───────────────────────────────────────────────────────────

docs-body:
  type: Section
  spatial:
    display: grid
    # Left pane width is driven by the single canonical --docs-tree-width custom
    # property (set by xp87-docs-splitter, persisted to localStorage). The middle
    # column is the draggable splitter rail: an explicit 0.375rem track (an `auto`
    # track collapses to 0 because the custom-element wrapper has no intrinsic
    # inline size). The splitter fills it; its ±0.25rem hit area widens the grab
    # zone. Default 18rem matches the var fallback.
    columns: "var(--docs-tree-width, 18rem) 0.375rem 1fr"
    # Explicit single row track. Without it the implicit row is `auto` (content
    # sized), which leaves the splitter cell's height indefinite — the bar's
    # block-size:100% then resolves to 0 and the rail becomes unhittable.
    # minmax(0,1fr) gives a definite full-height track that can still shrink so
    # the panes scroll.
    rows: "minmax(0, 1fr)"
    # flex:1 + min_height:0 — fill the space below the page header and let the
    # grid shrink so the panes (not the whole page) own the scroll.
    flex: 1
    min_height: 0rem
    overflow: hidden
    responsive:
      sm:
        # Single column; three rows for left-pane / collapsed-splitter / right-pane.
        # The splitter hides at this breakpoint (0-height middle row), so the panes
        # stack: tree on top (auto), viewer below (1fr).
        columns: 1fr
        rows: auto auto 1fr
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - docs-left-pane
    - docs-splitter
    - docs-right-pane

# ── Left pane: search box + doc tree ────────────────────────────────────────

docs-left-pane:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.sm
    padding: $spacing.md
    overflow: hidden
    height: 100%
    min_height: 0rem
  tokens:
    background: $color_scheme.light.color.surface
  a11y:
    role: complementary
    label: $t.docs.sidebar_label
  children:
    - docs-search-box
    - docs-tree
    - docs-local-procedures-region

# The slot's bounded region. A tenant's widget is mounted by this region in
# place of docs-local-procedures, so it is a child of the region for layout —
# without the region a long procedure would be a flex item of the pane with its
# full content height, and the tree above it would shrink to nothing. The
# region takes at most half the pane and scrolls its own content, leaving the
# tree its share. Unfilled, it has no height; the pane's gap still spaces it.
docs-local-procedures-region:
  type: Section
  spatial:
    flex: 0 1 auto
    max_height: 50%
    min_height: 0rem
    overflow_y: auto
  a11y:
    role: region
    label: $t.docs.local_procedures_label
  children:
    - docs-local-procedures

# The page's one named slot: a node whose default content is empty, so a tenant's
# effective UiCustomization targeting this node's id mounts its procedure viewer
# here and nothing renders while none does. Its accepts list is the only widget
# family a customization may put in its place.
# No a11y: block — a Slot renders only its fallback or what fills it, and the
# region above carries the label, so XP85113 refuses one here.
docs-local-procedures:
  type: Slot
  name: local-procedures
  accepts: [xp87-md-view]

docs-search-box:
  type: Input
  input_type: search
  placeholder: $t.docs.search_placeholder
  a11y:
    label: $t.docs.search_label
  # The bound input emits xp85-filter-changed { field: search_query, value } on
  # each keystroke. xp87-docs-tree listens for that event and filters the loaded
  # tree by title instantly (no fetch). Full-text content search is a separate,
  # explicit action (the tree's "search page contents" button → docs-fulltext-search).
  # The value reads the page state, so a reload with ?q= shows the query it seeded.
  bind:
    value: $state.search_query
  # No tokens: block — host-level border/radius overrides draw a second box
  # around the inner <input>, which already carries the standard emitted
  # input styling (border, radius, background, focus outline, 2.75rem touch
  # target). The standard styling is the single canonical path.
  spatial:
    min_height: $size.touch-target

# The doc tree renders the tree JSON from /api/docs/tree.
# It is a hand-authored Lit element that handles keyboard navigation
# (arrow keys, Enter to select) per WCAG 2.2 AA tree pattern.
# When search results are present it renders those instead. All three arrive
# as params bound from the page data; the highlighted leaf is the open
# document, `page.path`, with the folders above it expanded.
docs-tree:
  type: Widget
  ref: xp87-docs-tree
  params:
    tree: $data.tree
    search-results: $data.search_results
    page: $data.page
  spatial:
    flex: 1
    min_height: 0rem
    overflow_y: auto

# ── Splitter: draggable divider between the panes ────────────────────────────
# Hand-authored widget (xp87-docs-splitter). Resizes docs-left-pane by
# writing the --docs-tree-width custom property read by docs-body's grid. Hidden
# at the sm breakpoint (single column) where resize is disabled. Registered in
# ui/src/xp85.config.yaml custom_scripts.
docs-splitter:
  type: Widget
  ref: xp87-docs-splitter

# ── Right pane: Markdown viewer ──────────────────────────────────────────────

docs-right-pane:
  type: Section
  spatial:
    display: flex
    direction: column
    height: 100%
    min_height: 0rem
    overflow: hidden
  children:
    - docs-page-empty-state
    - docs-page-viewer

# Empty state shown when no page has been selected yet.
docs-page-empty-state:
  type: EmptyState
  icon: $icon.book-open
  title: $t.docs.empty_state_title
  body: $t.docs.empty_state
  a11y:
    label: $t.docs.empty_state_title
  visible_when: "!state.selected_path"

# md-view component: renders the raw Markdown body.
# body is bound to the fetched page data; `page` gives it the document's own path,
# against which it resolves relative in-content links.
# An in-content link click only dispatches docs-select { path } (the same event
# the tree emits); md-view writes no URL. The page's docs-select handler updates
# state.selected_path, writes ?path= into the #/docs hash route with one history
# entry, and triggers a new /api/docs/page fetch (via the page data block above).
# A ?path= deep link seeds state.selected_path from the URL, so it opens on that
# document (#2062).
docs-page-viewer:
  type: Widget
  ref: xp87-md-view
  params:
    body: $data.page.body
    page: $data.page
  visible_when: "state.selected_path"
  spatial:
    flex: 1
    min_height: 0rem
    overflow: auto
